import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { setRatingsCache, addRating, updateRating } from '../Redux/store';
import { loadFromCache, CACHE_KEYS, updateCacheTimestamp } from '../data/cache';

const PAGE_SIZE = 20;

/**
 * Hook to load and cache ratings for a user
 *
 * Flow (three-tier cache):
 * 1. Check Redux - if found, set data state immediately (no loading)
 * 2. Check localStorage - if found, set data state immediately, set loading true, reload in background
 * 3. If not in either, set loading true, load from Supabase
 *
 * Cache format: { userId: { data: ratings, last_accessed: timestamp } }
 * Cache filtering: Happens when saving new data
 * - Removes entries older than CACHE_TTL_DAYS (7 days)
 *
 * @param {string} userId - The user ID to load ratings for
 * @param {number} nMessages - Number of ratings to load initially (default 20)
 * @returns {object} - { ratings, loading, avg, count, reload, submitRating, loadMore, hasMore }
 */
export default function useRatingData(userId, nMessages = 20) {
  const dispatch = useDispatch();
  const ratingsCache = useSelector((s) => s.ratings.ratingsCache);
  const authUserId = useSelector((s) => s.user.authUserId);

  const [ratings, setRatings] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadRatings = useCallback(async (pageNum = 0, append = false, isBackground = false) => {
    if (!userId) return;

    // Show loading spinner when loading from Supabase (not background)
    if (!isBackground) {
      setLoading(true);
    }

    try {
      const { data, error } = await supabase
        .from('ratings')
        .select('*')
        .eq('donor_id', userId)
        .order('created_at', { ascending: false })
        .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

      if (error) throw error;

      const newRatings = data || [];

      if (append) {
        setRatings((prev) => [...prev, ...newRatings]);
      } else {
        setRatings(newRatings);
        // Update Redux cache with initial page
        dispatch(setRatingsCache({ userId, ratings: newRatings }));
        // Update localStorage cache with new format
        const cache = loadFromCache(CACHE_KEYS.RATINGS) || {};
        cache[userId] = { data: newRatings, last_accessed: Date.now() };
        localStorage.setItem(CACHE_KEYS.RATINGS, JSON.stringify(cache));
      }

      setHasMore(newRatings.length === PAGE_SIZE);
    } catch (err) {
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [userId]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadRatings(nextPage, true);
    }
  }, [hasMore, loading, page, loadRatings]);

  const submitRating = useCallback(async (ratingData) => {
    if (!userId || !authUserId) return;

    const { stars, comment, met_in_person } = ratingData;

    // Check if user already rated
    const existing = ratingsCache[userId]?.find(r => r.rater_id === authUserId);

    try {
      if (existing) {
        // Update existing rating
        const { data, error } = await supabase
          .from('ratings')
          .update({ stars, comment, met_in_person })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;

        dispatch(updateRating({ userId, ratingId: existing.id, rating: data }));
        setRatings((prev) => prev.map(r => r.id === data.id ? data : r));

        // Update cache
        const cache = loadFromCache(CACHE_KEYS.RATINGS) || {};
        if (cache[userId]) {
          cache[userId].data = ratings.map(r => r.id === data.id ? data : r);
          cache[userId].last_accessed = Date.now();
          localStorage.setItem(CACHE_KEYS.RATINGS, JSON.stringify(cache));
        }
      } else {
        // Insert new rating
        const { data, error } = await supabase
          .from('ratings')
          .insert({
            rater_id: authUserId,
            donor_id: userId,
            stars,
            comment,
            met_in_person
          })
          .select()
          .single();

        if (error) throw error;

        dispatch(addRating({ userId, rating: data }));
        setRatings((prev) => [data, ...prev]);

        // Update cache
        const cache = loadFromCache(CACHE_KEYS.RATINGS) || {};
        if (cache[userId]) {
          cache[userId].data = [data, ...ratings];
          cache[userId].last_accessed = Date.now();
          localStorage.setItem(CACHE_KEYS.RATINGS, JSON.stringify(cache));
        }
      }

      return true;
    } catch (err) {
      return false;
    }
  }, [userId, authUserId, ratingsCache, ratings]);

  const reload = useCallback(() => {
    setPage(0);
    loadRatings(0, false);
  }, [userId]);

  // Initialize from cache immediately (three-tier cache)
  useEffect(() => {
    if (!userId) return;

    // First try Redux cache (freshest, session-tracked)
    if (ratingsCache[userId]) {
      setRatings(ratingsCache[userId]);
      return;
    }

    // Then try localStorage cache
    const cache = loadFromCache(CACHE_KEYS.RATINGS) || {};
    const cachedEntry = cache[userId];
    const cachedRatings = cachedEntry?.data;

    if (cachedRatings) {
      // Update last_accessed timestamp
      updateCacheTimestamp(CACHE_KEYS.RATINGS, userId);
      // Set immediately for instant display
      setRatings(cachedRatings);
      // Update Redux
      dispatch(setRatingsCache({ userId, ratings: cachedRatings }));
      // Trigger background reload
      loadRatings(0, false, true);
      return;
    }

    // Not in cache - load from Supabase
    loadRatings(0, false, false);
  }, [userId]);

  const avg = ratings.length ? ratings.reduce((s, r) => s + r.stars, 0) / ratings.length : 0;

  return {
    ratings,
    avg,
    count: ratings.length,
    loading,
    reload,
    submitRating,
    loadMore,
    hasMore,
  };
}
